/**
 * Today's pure readings of the wire — no React, no messages — so each rule
 * has a Node test (`__tests__/model.test.ts`).
 *
 * - {@link greetingOf}: morning, afternoon or night from a local hour.
 * - {@link navBadgesOf}: the shell's two dots from `GET /me/pending`.
 * - {@link insightTarget}: where an insight's action goes.
 * - {@link insightParams}: an insight's numbers, formatted for the locale.
 * - {@link estimateMinutes}: the hero's minutes, or `null` when no honest
 *   estimate exists.
 * - {@link weekDays}: the week strip's seven days and their states.
 * - {@link sorenessBody}: the check-in's answer as the contract's input.
 */
import {
  type AppLocale,
  formatKg,
  formatWeight,
  toCalendarDay,
} from "@repo/client/format";
import type { WeightUnit } from "@repo/contract";
import type {
  InsightAction,
  PendingOutput,
  PlanItemView,
  PlanSessionView,
  SorenessInput,
  WorkoutSessionView,
} from "@repo/contract/training";
import type { Search } from "../../app/search";

// ── Greeting ────────────────────────────────────────────────────────────────

export type DayPart = "morning" | "afternoon" | "night";

/**
 * 05:00–11:59 is morning, 12:00–17:59 afternoon, and the rest night: someone
 * opening the app at 02:00 is still in the night, not in the morning.
 */
export function greetingOf(hour: number): DayPart {
  if (hour >= 5 && hour < 12) {
    return "morning";
  }
  if (hour >= 12 && hour < 18) {
    return "afternoon";
  }
  return "night";
}

// ── Nav badges ──────────────────────────────────────────────────────────────

export interface NavBadgeFlags {
  readonly planDraft: boolean;
  readonly trainerUnread: boolean;
}

/** Nothing known yet (no cache, first load, a failed read) is no badge. */
export function navBadgesOf(pending: PendingOutput | undefined): NavBadgeFlags {
  return {
    planDraft: pending !== undefined && pending.draft !== null,
    trainerUnread: pending !== undefined && pending.trainerUnread !== null,
  };
}

// ── Insight ─────────────────────────────────────────────────────────────────

export type InsightTarget =
  | { readonly kind: "open"; readonly search: Partial<Search> }
  | { readonly kind: "checkin"; readonly sessionId: string };

/**
 * `open_exercise` opens the exercise sheet (for a plan exercise, with the
 * plan's actions, D181), `ask_trainer` the trainer with the question in the
 * composer, `open_plan` the plan screen. `open_checkin` stays on Today: the card is
 * here, so the screen scrolls to it (and brings it back if it was put off).
 */
export function insightTarget(action: InsightAction): InsightTarget {
  switch (action.type) {
    case "open_exercise":
      return {
        kind: "open",
        search: { ex: action.exerciseId, sheet: "exercise" },
      };
    case "ask_trainer":
      return { kind: "open", search: { ask: action.ask, overlay: "coach" } };
    case "open_plan":
      return { kind: "open", search: { screen: "plan" } };
    case "open_checkin":
      return { kind: "checkin", sessionId: action.sessionId };
    default: {
      const unknown: never = action;
      return unknown;
    }
  }
}

/**
 * The server sends numbers raw (T36: `2.5`, never `2,5`). A fraction is
 * written in the locale here, up to two digits (a 1.25 kg step stays 1,25);
 * an integer stays a NUMBER, because a plural selector (`remaining`,
 * `weeks`) matches on it.
 *
 * A param named `…Kg` (`stepKg`, `e1rmKg`) is a load in kilograms, and also
 * arrives as `…Text` in the person's unit with the unit word (`D266`) —
 * "+5 lb", "100 kg". The messages quote the `…Text` twin, so a sentence
 * never says "kg" beside a pound figure; the raw key stays for any message
 * that counts on it.
 */
export function insightParams(
  params: Readonly<Record<string, string | number>>,
  locale: AppLocale,
  unit: WeightUnit = "kg"
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    out[key] =
      typeof value === "number" && !Number.isInteger(value)
        ? formatKg(value, locale, { maxFractionDigits: 2 })
        : value;
    if (typeof value === "number" && key.endsWith("Kg")) {
      out[`${key.slice(0, -2)}Text`] = formatWeight(value, unit, locale, {
        maxFractionDigits: 2,
      });
    }
  }
  return out;
}

// ── The hero's minutes ──────────────────────────────────────────────────────

/** Seconds per repetition when only reps are prescribed: a controlled
 *  two-to-three-second rep, the tempo the engine's prescriptions assume. */
export const SECONDS_PER_REP = 3;

type MinutesItem = Pick<
  PlanItemView,
  "restSeconds" | "targetDurationSeconds" | "targetRepsMax" | "targetSets"
>;

/**
 * The session's length in minutes: the warm-up, plus for every item
 * `sets × (work + rest)`, where work is the hold, or the top of the rep range
 * at {@link SECONDS_PER_REP}. Rounded up to the next 5.
 *
 * `null` — show exercises and sets only — when any item lacks a number the
 * sum needs (sets, rest, or both reps and hold): a total that silently skips
 * an exercise is a made-up total.
 */
export function estimateMinutes(
  session: Pick<PlanSessionView, "warmup"> & {
    readonly items: readonly MinutesItem[];
  }
): number | null {
  if (session.items.length === 0) {
    return null;
  }
  let seconds = 0;
  for (const item of session.items) {
    const work =
      item.targetDurationSeconds ??
      (item.targetRepsMax === null
        ? null
        : item.targetRepsMax * SECONDS_PER_REP);
    if (
      item.targetSets === null ||
      item.restSeconds === null ||
      work === null
    ) {
      return null;
    }
    seconds += item.targetSets * (work + item.restSeconds);
  }
  const minutes = session.warmup.minutes + seconds / 60;
  return Math.ceil(minutes / 5) * 5;
}

/** A session's working sets: the sum of the items' `targetSets`. */
export function setsIn(session: {
  readonly items: readonly Pick<PlanItemView, "targetSets">[];
}): number {
  return session.items.reduce((n, item) => n + (item.targetSets ?? 0), 0);
}

// ── The week strip ──────────────────────────────────────────────────────────

/**
 * - `done`: a session was trained that day (ended, or still running today).
 * - `today`: nothing trained yet today.
 * - `planned`: a later day this week that a session is fixed to.
 * - `rest`: a day no session is fixed to, in a plan with fixed days.
 * - `free`: any other day of a plan whose sessions have no fixed day (a
 *   variable week): calling it "rest" would be a claim the plan never made.
 */
export type DayState = "done" | "today" | "planned" | "rest" | "free";

export interface WeekDay {
  /** Day of the month. */
  readonly date: number;
  /** `YYYY-MM-DD`, local. */
  readonly day: string;
  /** For a `done` day, the plan session trained last that day (or `null`
   *  off-plan); for `today`/`planned`, the session fixed to it, if any. */
  readonly planSessionId: string | null;
  /** When the day's last session started (ISO), for a `done` day. */
  readonly startedAt: string | null;
  readonly state: DayState;
  /** 0 = Sunday … 6 = Saturday (`Date#getDay`, the contract's weekday). */
  readonly weekday: number;
}

const DAY_MS = 86_400_000;

/**
 * Sunday 00:00 local of `now`'s week. The strip DRAWS the week from Sunday,
 * as a Brazilian calendar does; nothing that counts or schedules reads this
 * (the stats and the retrospective keep ISO weeks, server-side).
 */
export function weekStartOf(now: Date): Date {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate() - start.getDay());
  return start;
}

/** The week's `from`/`to` for `GET /me/sessions` (inclusive calendar days). */
export function weekRangeOf(now: Date): { from: string; to: string } {
  const start = weekStartOf(now);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { from: toCalendarDay(start), to: toCalendarDay(end) };
}

type WeekSession = Pick<
  WorkoutSessionView,
  "abandoned" | "endedAt" | "planSessionId" | "sessionId" | "startedAt"
>;

type WeekPlanSession = Pick<PlanSessionView, "sessionId" | "weekday">;

/**
 * Sunday to Saturday of `now`'s week. A day is `done` when a session STARTED
 * on it (local day, like `chooseTodaysSession`); a session never started is
 * not on the wire. Days before today with nothing trained read `rest`/`free`
 * as the plan says — a missed fixed day is not "planned" any more.
 * An abandoned session is not a workout (`D177`) and marks no day done;
 * absent, as every older server sends it, the flag means not abandoned.
 */
export function weekDays(input: {
  readonly now: Date;
  readonly planSessions: readonly WeekPlanSession[];
  readonly sessions: readonly WeekSession[];
}): WeekDay[] {
  const start = weekStartOf(input.now);
  const today = toCalendarDay(input.now);
  const fixed = input.planSessions.some((s) => s.weekday !== null);
  const trained = new Map<string, WeekSession>();
  for (const session of input.sessions) {
    const at = Date.parse(session.startedAt);
    if (session.abandoned === true || !Number.isFinite(at)) {
      continue;
    }
    const key = toCalendarDay(new Date(at));
    const seen = trained.get(key);
    if (seen === undefined || Date.parse(seen.startedAt) < at) {
      trained.set(key, session);
    }
  }
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(start.getTime() + i * DAY_MS + DAY_MS / 2);
    date.setHours(0, 0, 0, 0);
    const day = toCalendarDay(date);
    const weekday = date.getDay();
    const done = trained.get(day);
    const fixedSession =
      input.planSessions.find((s) => s.weekday === weekday) ?? null;
    let state: DayState;
    if (done !== undefined) {
      state = "done";
    } else if (day === today) {
      state = "today";
    } else if (day > today && fixedSession !== null) {
      state = "planned";
    } else {
      state = fixed ? "rest" : "free";
    }
    return {
      date: date.getDate(),
      day,
      planSessionId:
        done === undefined
          ? (fixedSession?.sessionId ?? null)
          : done.planSessionId,
      startedAt: done === undefined ? null : done.startedAt,
      state,
      weekday,
    };
  });
}

// ── The check-in ────────────────────────────────────────────────────────────

export type SorenessLevel = SorenessInput["overall"];

export const SORENESS_LEVELS: readonly SorenessLevel[] = [
  "none",
  "mild",
  "moderate",
  "intense",
];

/**
 * Where it hurts, in the words people use, and the body-map regions
 * (`BODY_REGIONS`) each one stands for. "Legs" is the hips: the map has
 * joints, not thighs, and the hips are where a leg day's soreness sits.
 */
export const SORE_AREAS = {
  abdomen: ["abdomen"],
  back: ["thoracic"],
  chest: ["chest"],
  legs: ["hip_r", "hip_l"],
  lower_back: ["lumbar"],
  shoulders: ["shoulder_r", "shoulder_l"],
} as const satisfies Record<
  string,
  readonly NonNullable<SorenessInput["regions"]>[number]["region"][]
>;

export type SoreArea = keyof typeof SORE_AREAS;

export const SORE_AREA_ORDER: readonly SoreArea[] = [
  "chest",
  "back",
  "lower_back",
  "shoulders",
  "legs",
  "abdomen",
];

/**
 * The region intensity (1–5) each level stands for. `intense` is 4, the
 * engine's `SORENESS_INTENSE_THRESHOLD`: that is the level it reacts to.
 */
const INTENSITY: Record<Exclude<SorenessLevel, "none">, number> = {
  intense: 4,
  mild: 2,
  moderate: 3,
};

/** The PUT body. No area, or no soreness, sends `overall` alone. */
export function sorenessBody(
  level: SorenessLevel,
  area: SoreArea | null
): SorenessInput {
  if (level === "none" || area === null) {
    return { overall: level };
  }
  const intensity = INTENSITY[level];
  return {
    overall: level,
    regions: SORE_AREAS[area].map((region) => ({ intensity, region })),
  };
}

/**
 * The inverse of {@link sorenessBody}: what the person answered, read back
 * out of a body.
 *
 * A check-in paused offline is restored from the persisted cache with its
 * VARIABLES and nothing else (`soreness-mutation.ts`), so this is what gives
 * the after-answer note its words after a restart — without putting the
 * answer in the request, which `sorenessInput` is strict about.
 *
 * `none`, and a body with no regions or with regions no area claims, read as
 * no area: exactly what `sorenessBody` wrote for them.
 */
export function answerOf(input: SorenessInput): {
  readonly area: SoreArea | null;
  readonly level: SorenessLevel;
} {
  const regions = new Set((input.regions ?? []).map((entry) => entry.region));
  const area =
    SORE_AREA_ORDER.find(
      (candidate) =>
        SORE_AREAS[candidate].length === regions.size &&
        SORE_AREAS[candidate].every((region) => regions.has(region))
    ) ?? null;
  return { area, level: input.overall };
}

/** Which after-answer note: none, "mild is normal", or "the plan adapts". */
export function checkinNoteOf(
  level: SorenessLevel
): "none" | "mild" | "strong" {
  if (level === "none") {
    return "none";
  }
  return level === "mild" ? "mild" : "strong";
}

/**
 * The session the check-in asks about: the pending one, else the one the
 * insight requested; `null` when it was answered on this device or put off
 * (a request brings a put-off card back).
 */
export function checkinSessionOf(
  pending: string | null,
  device: {
    readonly answered: readonly string[];
    readonly dismissed: readonly string[];
    readonly requested: string | null;
  }
): string | null {
  const id = pending ?? device.requested;
  if (id === null || device.answered.includes(id)) {
    return null;
  }
  if (device.dismissed.includes(id) && device.requested !== id) {
    return null;
  }
  return id;
}
